import './UserMenu.scss'
import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent
} from 'react'
import { MdOutlineAccountCircle, MdOutlineCheck, MdOutlineLogout } from 'react-icons/md'
import { setColorScheme, useColorScheme, type ColorScheme } from '../colorScheme'
import { Divider, observeMovement, placeAnchored, Portal, pushEscape, rectOf } from '../../ui'

export interface UserMenuProps {
  /** The signed-in user's name (MSAL username). */
  name?: string | null | undefined
  onLogout: () => void
}

type FocusTarget = 'first' | 'last'

/** "ada.lovelace@example.com" → "AL"; "Ada" → "A". Decorative only. */
export function initialsOf(name: string): string {
  const parts = (name.split('@')[0] ?? '').split(/[.\s_-]+/).filter(Boolean)
  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''
  return (first + last).toUpperCase()
}

/**
 * WAI-ARIA menu button: the user's name (an account icon only below
 * `tablet`; the name stays its accessible name) opening a menu with
 * "Logout", as the portal's `<UserMenu><Logout/></UserMenu>`.
 */
export function UserMenu({ name, onLogout }: UserMenuProps) {
  const id = useId()
  const buttonId = `user-menu-button-${id}`
  const menuId = `user-menu-${id}`
  const [isOpen, setOpen] = useState(false)
  const scheme = useColorScheme()
  const buttonRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)
  const focusTarget = useRef<FocusTarget>('first')

  const items = () =>
    Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]') ?? [])

  const open = (target: FocusTarget) => {
    focusTarget.current = target
    setOpen(true)
  }
  const close = (restoreFocus: boolean) => {
    setOpen(false)
    if (restoreFocus) buttonRef.current?.focus()
  }

  // Focus the first/last item, then keep the menu placed under its button.
  useLayoutEffect(() => {
    const anchor = buttonRef.current
    const menu = menuRef.current
    if (!isOpen || !anchor || !menu) return
    const list = Array.from(menu.querySelectorAll<HTMLElement>('[role^="menuitem"]'))
    ;(focusTarget.current === 'last' ? list.at(-1) : list[0])?.focus()
    return observeMovement(anchor, menu, () => {
      const p = placeAnchored(
        rectOf(anchor),
        { width: menu.offsetWidth, height: menu.offsetHeight },
        { width: window.innerWidth, height: window.innerHeight },
        { align: 'start' }
      )
      menu.style.setProperty('--user-menu-top', `${p.top}px`)
      menu.style.setProperty('--user-menu-left', `${p.left}px`)
    })
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    return pushEscape(() => {
      setOpen(false)
      buttonRef.current?.focus()
      return true
    })
  }, [isOpen])

  // A press outside closes without stealing focus from where it landed.
  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (menuRef.current?.contains(target) || buttonRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [isOpen])

  const onButtonKeyDown = (event: ReactKeyboardEvent) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      open(event.key === 'ArrowUp' ? 'last' : 'first')
    }
  }

  const onMenuKeyDown = (event: ReactKeyboardEvent) => {
    const list = items()
    const index = list.indexOf(document.activeElement as HTMLElement)
    const move = (next: number) => {
      event.preventDefault()
      list[(next + list.length) % list.length]?.focus()
    }
    if (event.key === 'ArrowDown') move(index + 1)
    else if (event.key === 'ArrowUp') move(index - 1)
    else if (event.key === 'Home') move(0)
    else if (event.key === 'End') move(list.length - 1)
    // Tab: close and put focus back on the button; the browser then moves
    // on from there, as if the menu had been inline.
    else if (event.key === 'Tab') close(true)
  }

  return (
    <>
      <button
        ref={buttonRef}
        id={buttonId}
        type="button"
        className="user-menu"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        onClick={() => {
          if (isOpen) close(false)
          else open('first')
        }}
        onKeyDown={onButtonKeyDown}
      >
        {name && initialsOf(name) ? (
          <span className="user-menu__avatar" aria-hidden="true">
            {initialsOf(name)}
          </span>
        ) : (
          <span className="user-menu__icon" aria-hidden="true">
            <MdOutlineAccountCircle />
          </span>
        )}
        <span className="user-menu__name">{name || 'Account'}</span>
      </button>
      {isOpen && (
        <Portal>
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            aria-labelledby={buttonId}
            className="user-menu__menu"
            onKeyDown={onMenuKeyDown}
          >
            <div role="group" aria-label="Theme" className="user-menu__group">
              <span className="user-menu__heading" aria-hidden="true">
                Theme
              </span>
              {(
                [
                  ['system', 'System'],
                  ['light', 'Light'],
                  ['dark', 'Dark']
                ] as [ColorScheme, string][]
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="menuitemradio"
                  aria-checked={scheme === value}
                  tabIndex={-1}
                  className="user-menu__item"
                  onClick={() => {
                    setColorScheme(value)
                  }}
                >
                  <span className="user-menu__item-icon" aria-hidden="true">
                    {scheme === value && <MdOutlineCheck />}
                  </span>
                  {label}
                </button>
              ))}
            </div>
            <Divider spacing="sm" />
            <button
              type="button"
              role="menuitem"
              tabIndex={-1}
              className="user-menu__item"
              onClick={() => {
                close(false)
                onLogout()
              }}
            >
              <span className="user-menu__item-icon" aria-hidden="true">
                <MdOutlineLogout />
              </span>
              Logout
            </button>
          </div>
        </Portal>
      )}
    </>
  )
}
